import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Window-level file drop.
 *
 * ページ内の一部だけに onDrop を付けると、余白・ヘッダー・コンテンツ外に
 * ドロップされた瞬間にブラウザの既定動作が働き、PDFが新しいタブで開いて
 * しまう（アップロードされない）。ドラッグ＆ドロップはウィンドウ全体で
 * 受け取り、必ず preventDefault する。
 *
 * @param {boolean} enabled  受け入れ可能か（権限など）
 * @param {(files: FileList) => void} onFiles  ファイルが落ちたときの処理
 * @returns {{ dragging: boolean }}
 */
export default function useFileDrop(enabled, onFiles) {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  const cb = useRef(onFiles);
  cb.current = onFiles;

  const hasFiles = useCallback((e) => {
    const dt = e.dataTransfer;
    if (!dt) return false;
    if (dt.types && Array.from(dt.types).includes('Files')) return true;
    return !!(dt.items && Array.from(dt.items).some((i) => i.kind === 'file'));
  }, []);

  // 専用のドロップ領域（AI取り込みモーダル、チャットの添付、file input）が
  // すでに処理した場合は二重に取り込まない。
  const claimedByOthers = useCallback((e) => {
    // 既定動作の抑止が「アプリ全体のガード」由来なら未処理として扱う。
    if (e.defaultPrevented && !e.__sensbeyGuardOnly) return true;
    const t = e.target;
    if (t && t.closest && t.closest('input[type="file"], [data-native-drop]')) return true;
    return false;
  }, []);

  useEffect(() => {
    if (!enabled) { setDragging(false); depth.current = 0; return undefined; }

    const onEnter = (e) => {
      if (!hasFiles(e) || claimedByOthers(e)) return;
      e.preventDefault();
      depth.current += 1;
      setDragging(true);
    };
    const onOver = (e) => {
      if (!hasFiles(e)) return;
      if (claimedByOthers(e)) { if (dragging) setDragging(false); return; }
      e.preventDefault();               // これが無いと drop が発火しない
      try { e.dataTransfer.dropEffect = 'copy'; } catch { /* noop */ }
      if (!dragging) setDragging(true);
    };
    const onLeave = (e) => {
      if (!hasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      // ウィンドウ外に出たときだけ解除する（子要素間の移動では消さない）
      if (depth.current === 0 || !e.relatedTarget) setDragging(false);
    };
    const onDrop = (e) => {
      if (!hasFiles(e)) return;
      depth.current = 0;
      setDragging(false);
      if (claimedByOthers(e)) return;   // 専用領域が処理済み
      e.preventDefault();               // ブラウザがPDFを開くのを防ぐ
      const files = e.dataTransfer?.files;
      if (files && files.length) cb.current(files);
    };
    const onWindowLeave = () => { depth.current = 0; setDragging(false); };

    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragover', onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    window.addEventListener('blur', onWindowLeave);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('blur', onWindowLeave);
    };
  }, [enabled, dragging, hasFiles, claimedByOthers]);

  return { dragging };
}
